import React, { useEffect } from "react";
import { Card, Typography, Box, CircularProgress, Divider, Rating, Pagination, Grid } from "@mui/material";
import chaletApi from "api/chaletApi";
import { Branch } from "types/Branch";

type FeedbackViewModalProps = {
  branch: Branch;
};

const FeedbackViewModal: React.FC<FeedbackViewModalProps> = ({ branch }) => {
  const [getFeedbacksByBranch, { data, isError, isLoading }] = chaletApi.useLazyGetFeedbackByBranchQuery();
  const [page, setPage] = React.useState(1);

  useEffect(() => {
    if (branch) {
      getFeedbacksByBranch({
        branchId: branch.id,
        page,
        limit: 10,
      });
    }
  }, [branch, page]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h6" color="error">
          Error loading feedbacks.
        </Typography>
      </Box>
    );
  }

  return data?.data ? (
    <Card
      sx={{
        width: "90%",
        maxWidth: 700,
        maxHeight: "80vh",
        p: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
      }}
    >
      <Box sx={{ overflowY: "auto", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Feedbacks de sucursal {branch.name}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {data.data.feedbacks
            .slice()
            .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((feedback: any) => (
              <Grid item xs={12} sm={6} key={feedback.id}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.08)",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    height: "90%",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Feedback para Orden #{feedback.orderId}
                  </Typography>

                  <Typography variant="caption" color="textSecondary" mb={1}>
                    <b>Fecha:</b>{" "}
                    {new Date(feedback.createdAt).toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>

                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Feedback de la Comida
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Rating value={feedback.foodFeedback.rating} precision={0.5} readOnly />
                    </Box>
                    {feedback.foodFeedback.comments ? (
                      <Typography variant="caption" color="textSecondary">
                        {feedback.foodFeedback.comments}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Sin comentarios para la comida.
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Feedback de la Entrega
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Rating value={feedback.deliveryFeedback.rating} precision={0.5} readOnly />
                    </Box>
                    {feedback.deliveryFeedback.comments ? (
                      <Typography variant="caption" color="textSecondary">
                        {feedback.deliveryFeedback.comments}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Sin comentarios para la entrega.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
        </Grid>

        {data.data.feedbacks.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              py: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Box
              sx={{
                fontSize: 64,
                color: "primary.main",
                mb: 2,
              }}
            >
              <span role="img" aria-label="No feedback">
                📭
              </span>
            </Box>
            <Typography variant="h6" fontWeight="bold">
              No hay feedback disponible
            </Typography>
            <Typography variant="body2" mt={1}>
              Parece que esta sucursal aún no tiene comentarios. <br />
              Intenta más tarde para ver actualizaciones.
            </Typography>
          </Box>
        )}
      </Box>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination count={data.data.totalPages || 1} page={page} onChange={handlePageChange} color="primary" />
      </Box>
    </Card>
  ) : null;
};

export default FeedbackViewModal;
